<template>
    <div v-if="isFirstLoading" class="export-list-loading">
        <preloader :display="true" />
    </div>
    <div v-else>
        <div v-if="exports.length > 0">
            <div class="export-list-header">
                <h6 class="export-list-header__title">
                    {{ $filters.upperCase($t.__('export.notifications.recentExports')) }}
                </h6>
            </div>
            <div class="export-list-container">
                <export-item v-for="item in exports" :key="item.id" :item="item" />

                <div class="no-more-container">
                    <div class="no-more-content">
                        <CompassButton
                            :label="$t.__('export.notifications.viewAll')"
                            :type="CompassEnums.ButtonType.SECONDARY"
                            :size="CompassEnums.ButtonSize.MEDIUM"
                            @click.prevent="goToExportsPage"
                        />
                    </div>
                </div>
            </div>
        </div>

        <div v-else class="export-no-result">
            <img
                src="/ressources/logged/img/illustrations/no-results.svg"
                alt=""
                width="184"
                height="184"
            />
            <div class="export-no-result__text">
                <h4 class="export-no-result__title">
                    {{ $t.__('export.notifications.noRecentExports') }}
                </h4>

                <CompassButton
                    :label="$t.__('export.notifications.viewAll')"
                    :type="CompassEnums.ButtonType.SECONDARY"
                    :size="CompassEnums.ButtonSize.MEDIUM"
                    @click.prevent="goToExportsPage"
                />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, inject, onMounted, onUnmounted } from 'vue';
import type { Translator } from 'types/injects/translator.type';
import type { PushToast } from 'types/injects/pushToast.type';
import type { ExportItem as ExportItemType } from 'api/notification/Export';
import ExportItem from 'vcomponents/notifications/ExportItem.vue';
import Preloader from 'vcomponents/utilities/Preloader.vue';
import ExportApi from 'api/notification/Export';

const $t = inject('$t') as Translator;
const $pushToast = inject('$pushToast') as PushToast;

const exports = ref<ExportItemType[]>([]);
const isFirstLoading = ref(false);

const exportProgressChange = async ({ id }: { id: string }): Promise<void> => {
    const index = exports.value.findIndex((exportItem) => exportItem.id === id);
    if (index !== -1) {
        const response = await ExportApi.getExport(id);
        exports.value[index] = response.data;
    }
};

const getExports = async () => {
    try {
        isFirstLoading.value = true;
        const response = await ExportApi.getExports({});
        exports.value = response.data.data.filter(function (exportItem) {
            if (exportItem.status === 'succeed' && exportItem.public_url) {
                return true;
            }
            if (moment(exportItem.started_at).add(1, 'days').isAfter(moment())) {
                return true;
            }
            return false;
        });
    } catch {
        $pushToast({
            type: CompassEnums.ToastType.ERROR,
            icon: true,
            text: $t.__('export.notifications.fetchError'),
        });
    } finally {
        isFirstLoading.value = false;
    }
};

const goToExportsPage = () => {
    window.location.href = '/settings/exports';
};

onMounted(async () => {
    await getExports();
    EventBus.on('export:progress', exportProgressChange as (...args: unknown[]) => void);
});

onUnmounted(() => {
    EventBus.off('export:progress', exportProgressChange as (...args: unknown[]) => void);
});
</script>

<style lang="scss" scoped>
@use '~@sellsy/styleguide/src/_variables' as *;

.export-list-loading {
    height: 480px;
}

.export-list-container {
    height: 500px;
    margin-bottom: 0;
    overflow-y: auto;

    .no-more-container {
        padding: var(--spacing-1);
    }

    .no-more-content {
        text-align: center;
    }
}

.export-no-result {
    padding: 160px 40px;
    text-align: center;
    word-break: break-word;

    &__text {
        margin: auto;
        width: 360px;
    }

    &__title {
        color: var(--colors-common-text-standard-default);
        font-size: var(--font-size-17);
        margin-top: var(--spacing-5);
    }
}

.export-list-header {
    display: flex;
    justify-content: space-between;
    margin-bottom: var(--spacing-1);
    margin-top: var(--spacing-2);
    padding: 0 var(--spacing-2);

    &__title {
        color: var(--colors-common-text-standard-disabled);
        font-weight: 700;
        line-height: 16px;
        margin: 0;
    }
}
</style>
